import { can } from '@ambernord/shared';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  SkeletonRows,
  SlideOver,
  StatusBadge,
  cn,
  useToast,
} from '@ambernord/ui';
import { ArrowRight, Clock, FileQuestion, Handshake, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.js';
import { api } from '../../lib/api.js';
import { formatDate, formatNumber, productName } from '../../lib/format.js';
import { BreakdownTable } from './BreakdownTable.js';
import { ComposePanel } from './ComposePanel.js';
import {
  type AdminOfferDetail,
  OFFER_TABS,
  type OfferRow,
  type OfferTab,
  type ProductItem,
  byNewest,
  canManageOffers,
  errorKey,
  shortId,
  supplierLabel,
} from './types.js';

interface CustomerListItem {
  id: string;
  legalName: string;
}

interface CarrierProfileItem {
  tenantId: string;
  displayName: string | null;
}

/**
 * Platformas skats: ciļņi pa statusiem (Requested/Drafted/Sent/Approved ar skaitiem),
 * komponēšana caur kombināciju salīdzinājumu, nosūtīšana, apstiprināto saite uz pasūtījumu.
 * Marža/izmaksu komponentes šeit IR redzamas — admin projekcija (§4.9).
 */
export function AdminOffers() {
  const { t, i18n } = useTranslation('offers');
  const { t: tStatus } = useTranslation('statuses');
  const { toast } = useToast();
  const { user } = useAuth();
  const locale = i18n.language;
  const canManage = user ? canManageOffers(user.role) : false;
  // platform_finance nav 'carrier' atļaujas → pārvadātāju vārdus nepieprasa (rāda ID fragmentu).
  const canListCarriers = user ? can(user.role, 'carrier', 'list') : false;

  const [offers, setOffers] = useState<OfferRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [customerNames, setCustomerNames] = useState<Map<string, string>>(new Map());
  const [carrierNames, setCarrierNames] = useState<Map<string, string>>(new Map());
  const [tab, setTab] = useState<OfferTab>('quote_requested');
  const [composing, setComposing] = useState<OfferRow | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminOfferDetail | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ items?: OfferRow[] }>('/api/offers');
    if (!res.ok) {
      setError(t('errors.loadFailed'));
      return;
    }
    setError(null);
    setOffers([...(res.data.items ?? [])].sort(byNewest));
  }, [t]);

  useEffect(() => {
    void load();
    let active = true;
    void (async () => {
      const [productsRes, customersRes, carriersRes] = await Promise.all([
        api.get<{ items?: ProductItem[] }>('/api/products'),
        api.get<{ items?: CustomerListItem[] }>('/api/customers'),
        canListCarriers ? api.get<{ items?: CarrierProfileItem[] }>('/api/carriers') : null,
      ]);
      if (!active) return;
      if (productsRes.ok) setProducts(productsRes.data.items ?? []);
      if (customersRes.ok) {
        setCustomerNames(new Map((customersRes.data.items ?? []).map((c) => [c.id, c.legalName])));
      }
      if (carriersRes?.ok) {
        setCarrierNames(
          new Map(
            (carriersRes.data.items ?? [])
              .filter((c) => c.displayName)
              .map((c) => [c.tenantId, c.displayName as string]),
          ),
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [load, canListCarriers]);

  // Detaļu panelis: ielādē pilno sadalījumu (admin projekcija — costComponents + iemesli).
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    let active = true;
    void (async () => {
      const res = await api.get<AdminOfferDetail>(`/api/offers/${detailId}`);
      if (active && res.ok) setDetail(res.data);
    })();
    return () => {
      active = false;
    };
  }, [detailId]);

  const productMap = new Map(products.map((p) => [p.id, p]));
  const byTab = new Map<OfferTab, OfferRow[]>(OFFER_TABS.map((s) => [s, []]));
  for (const o of offers ?? []) {
    byTab.get(o.status as OfferTab)?.push(o);
  }
  const visible = byTab.get(tab) ?? [];

  async function send(offerId: string) {
    setSendingId(offerId);
    try {
      const res = await api.post<{ error?: string }>(`/api/offers/${offerId}/send`);
      if (!res.ok) {
        toast(t(errorKey(res.data.error)), 'error');
        return;
      }
      toast(t('toasts.sent'), 'success');
      await load();
    } finally {
      setSendingId(null);
    }
  }

  function customerLabel(offer: OfferRow): string {
    return customerNames.get(offer.customerId) ?? shortId(offer.customerId);
  }

  function carrierLabel(offer: OfferRow): string {
    if (!offer.carrierTenantId) return t('labels.supplierArranged');
    return carrierNames.get(offer.carrierTenantId) ?? shortId(offer.carrierTenantId);
  }

  const emptyByTab: Record<OfferTab, { title: string; hint: string }> = {
    quote_requested: { title: t('empty.requestedTitle'), hint: t('empty.requestedHint') },
    offer_drafted: { title: t('empty.draftedTitle'), hint: t('empty.draftedHint') },
    offer_sent: { title: t('empty.sentTitle'), hint: t('empty.sentHint') },
    offer_approved: { title: t('empty.approvedTitle'), hint: t('empty.approvedHint') },
  };
  const tabLabel: Record<OfferTab, string> = {
    quote_requested: t('tabs.requested'),
    offer_drafted: t('tabs.drafted'),
    offer_sent: t('tabs.sent'),
    offer_approved: t('tabs.approved'),
  };

  return (
    <div>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">{t('intro.platform')}</p>

      <div
        role="tablist"
        aria-label={t('title')}
        className="mb-5 flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-900"
      >
        {OFFER_TABS.map((s) => {
          const active = tab === s;
          const count = byTab.get(s)?.length ?? 0;
          return (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(s)}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
                active
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white',
              )}
            >
              {tabLabel[s]}
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs tabular-nums',
                  active
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/60 dark:text-brand-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
                )}
              >
                {offers === null ? '…' : count}
              </span>
            </button>
          );
        })}
      </div>

      {error ? (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : offers === null ? (
        <SkeletonRows rows={5} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={
            tab === 'quote_requested' ? (
              <FileQuestion className="h-6 w-6" aria-hidden />
            ) : (
              <Handshake className="h-6 w-6" aria-hidden />
            )
          }
          title={emptyByTab[tab].title}
          hint={emptyByTab[tab].hint}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {visible.map((offer) => (
            <Card key={offer.id}>
              <CardHeader
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    {offer.offerNo}
                    <StatusBadge status={offer.status} label={tStatus(offer.status)} />
                  </span>
                }
                subtitle={`${customerLabel(offer)} · ${t('labels.market')}: ${offer.market.toUpperCase()} · ${t('labels.created')}: ${formatDate(offer.createdAt, locale)}`}
              />
              <CardBody className="flex flex-col gap-3">
                {offer.status !== 'quote_requested' ? (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <dt className="text-slate-500 dark:text-slate-400">{t('labels.supplier')}</dt>
                    <dd>{supplierLabel(offer)}</dd>
                    <dt className="text-slate-500 dark:text-slate-400">{t('labels.carrier')}</dt>
                    <dd>{carrierLabel(offer)}</dd>
                    <dt className="text-slate-500 dark:text-slate-400">{t('labels.total')}</dt>
                    <dd className="font-semibold text-slate-900 dark:text-white">
                      {formatNumber(offer.total, locale)} {offer.currency ?? 'EUR'}
                    </dd>
                    <dt className="text-slate-500 dark:text-slate-400">{t('labels.margin')}</dt>
                    <dd>
                      {offer.marginPct != null && offer.marginPct !== ''
                        ? `${formatNumber(offer.marginPct, locale)}%`
                        : '—'}
                    </dd>
                  </dl>
                ) : offer.notes ? (
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {t('labels.notes')}: {offer.notes}
                  </p>
                ) : null}

                {offer.status === 'offer_sent' ? (
                  <p className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                    <Clock className="h-4 w-4" aria-hidden />
                    {t('labels.waitingCustomer')}
                    {offer.validUntil
                      ? ` · ${t('labels.validUntil')}: ${formatDate(offer.validUntil, locale)}`
                      : null}
                  </p>
                ) : null}

                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setDetailId(offer.id)}>
                    {t('actions.details')}
                  </Button>
                  {offer.status === 'quote_requested' && canManage ? (
                    <Button size="sm" onClick={() => setComposing(offer)}>
                      {t('actions.compose')}
                    </Button>
                  ) : null}
                  {offer.status === 'offer_drafted' && canManage ? (
                    <Button
                      size="sm"
                      disabled={sendingId === offer.id}
                      onClick={() => void send(offer.id)}
                    >
                      <Send className="h-4 w-4" aria-hidden />
                      {sendingId === offer.id ? t('loading') : t('actions.send')}
                    </Button>
                  ) : null}
                  {offer.status === 'offer_approved' ? (
                    <Link to="/orders">
                      <Button variant="secondary" size="sm">
                        {t('actions.goToOrders')}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Button>
                    </Link>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {composing ? (
        <ComposePanel
          offer={composing}
          productMap={productMap}
          carrierNames={carrierNames}
          onClose={() => setComposing(null)}
          onChanged={() => void load()}
        />
      ) : null}

      <SlideOver
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        title={detail ? detail.offer.offerNo : t('actions.details')}
        className="max-w-2xl"
      >
        {detail ? (
          <div className="flex flex-col gap-5">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-slate-500 dark:text-slate-400">{t('labels.customer')}</dt>
              <dd>
                {customerNames.get(detail.offer.customerId) ?? shortId(detail.offer.customerId)}
              </dd>
              <dt className="text-slate-500 dark:text-slate-400">{t('labels.market')}</dt>
              <dd>{detail.offer.market.toUpperCase()}</dd>
              <dt className="text-slate-500 dark:text-slate-400">{t('labels.supplier')}</dt>
              <dd>{supplierLabel(detail.offer)}</dd>
              <dt className="text-slate-500 dark:text-slate-400">{t('labels.carrier')}</dt>
              <dd>{carrierLabel(detail.offer)}</dd>
              {detail.offer.shippingMode ? (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('labels.mode')}</dt>
                  <dd>
                    {t(`modes.${detail.offer.shippingMode}`, {
                      defaultValue: detail.offer.shippingMode,
                    })}
                  </dd>
                </>
              ) : null}
              {detail.offer.incoterm ? (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('labels.incoterm')}</dt>
                  <dd>
                    {t(`incoterms.${detail.offer.incoterm}`, {
                      defaultValue: detail.offer.incoterm,
                    })}
                  </dd>
                </>
              ) : null}
              {detail.offer.sentAt ? (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('labels.sentAt')}</dt>
                  <dd>{formatDate(detail.offer.sentAt, locale)}</dd>
                </>
              ) : null}
              {detail.offer.approvedAt ? (
                <>
                  <dt className="text-slate-500 dark:text-slate-400">{t('labels.approvedAt')}</dt>
                  <dd>{formatDate(detail.offer.approvedAt, locale)}</dd>
                </>
              ) : null}
            </dl>

            {detail.lines.length > 0 ? (
              <section>
                <h3 className="mb-1 text-sm font-semibold text-slate-900 dark:text-white">
                  {t('labels.lines')}
                </h3>
                <ul className="text-sm text-slate-600 dark:text-slate-300">
                  {detail.lines.map((l) => (
                    <li key={l.id}>
                      {productName(productMap.get(l.productId)?.namesI18n, locale, l.productId)} —{' '}
                      {formatNumber(l.qty, locale)} {l.unit}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {detail.costComponents.length > 0 ? (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-white">
                  {t('labels.breakdown')}
                </h3>
                <BreakdownTable
                  components={detail.costComponents}
                  currency={detail.offer.currency ?? 'EUR'}
                  subtotal={detail.offer.subtotal}
                  total={detail.offer.total}
                />
              </section>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('labels.noBreakdown')}
              </p>
            )}
          </div>
        ) : (
          <SkeletonRows rows={6} />
        )}
      </SlideOver>
    </div>
  );
}
